import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ShippingEstimate } from '@pedix-workspace/utils';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { CurrencyFormatPipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { IconStoreComponent, IconHomeComponent } from '@pedix-workspace/angular-ui-icons';

export type ShippingOptionItem = {
  estimate: ShippingEstimate;
  label: string;
  price: number;
  deliveryTime: string;
  deliveryDays: number;
  isBestInGroup: boolean;
};

export type ShippingOptionGroup = {
  type: 'branch' | 'home';
  title: string;
  subtitle: string;
  icon: string;
  options: ShippingOptionItem[];
};

const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  correoargentino: 'Correo Argentino',
  andreani: 'Andreani',
  oca: 'OCA',
};

@Component({
  selector: 'pxw-shipping-options-selector',
  templateUrl: './shipping-options-selector.component.html',
  styleUrls: ['./shipping-options-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, FormsModule, CurrencyFormatPipe, IconStoreComponent, IconHomeComponent],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class ShippingOptionsSelectorComponent {
  private readonly t = inject(TranslocoService);

  readonly availableOptions = input.required<ShippingEstimate[]>();
  readonly selectedOption = input<ShippingEstimate | null>(null);
  readonly isFormSubmitted = input.required<boolean>();

  readonly selectedOptionChange = output<ShippingEstimate>();

  readonly selectedOptionInternal = signal<ShippingEstimate | null>(null);

  readonly providerDisplayName = computed(() => {
    const options = this.availableOptions();
    if (options.length === 0) return '';
    const providerName = options[0].providerName?.toLowerCase() || '';
    return PROVIDER_DISPLAY_NAMES[providerName] || providerName;
  });

  readonly shippingGroups = computed<ShippingOptionGroup[]>(() => {
    const options = this.availableOptions();
    if (options.length === 0) return [];

    const parsedOptions = options.map(estimate => {
      const additionalInfo = this.safeParseJson(estimate.additionalInfo);
      const deliveryDays = this.extractDeliveryDays(additionalInfo);

      return {
        estimate,
        additionalInfo,
        deliveryDays,
        price: estimate.price,
        deliveryType: estimate.deliveryType,
      };
    });

    const branchOptions = parsedOptions.filter(o => o.deliveryType === 'S');
    const homeOptions = parsedOptions.filter(o => o.deliveryType === 'D');

    const groups: ShippingOptionGroup[] = [];

    if (branchOptions.length > 0) {
      const minBranchPrice = Math.min(...branchOptions.map(o => o.price));
      groups.push({
        type: 'branch',
        title: this.t.translate('shippingOptions.branchPickupTitle'),
        subtitle: this.t.translate('shippingOptions.branchPickupSubtitle'),
        icon: 'store',
        options: branchOptions.map(o => ({
          estimate: o.estimate,
          label: this.buildServiceLabel(o.estimate, o.additionalInfo),
          price: o.price,
          deliveryTime: this.formatDeliveryTimeShort(o.additionalInfo),
          deliveryDays: o.deliveryDays,
          isBestInGroup: o.price === minBranchPrice,
        })),
      });
    }

    if (homeOptions.length > 0) {
      const optionsWithDays = homeOptions.filter(o => o.deliveryDays > 0);
      const minHomeDays = optionsWithDays.length > 0
        ? Math.min(...optionsWithDays.map(o => o.deliveryDays))
        : 0;
      const fastestHomeOptions = minHomeDays > 0
        ? homeOptions.filter(o => o.deliveryDays === minHomeDays)
        : homeOptions;
      const fastestCheapest = Math.min(...fastestHomeOptions.map(o => o.price));

      groups.push({
        type: 'home',
        title: this.t.translate('shippingOptions.homeDeliveryTitle'),
        subtitle: this.t.translate('shippingOptions.homeDeliverySubtitle'),
        icon: 'home',
        options: homeOptions.map(o => ({
          estimate: o.estimate,
          label: this.buildServiceLabel(o.estimate, o.additionalInfo),
          price: o.price,
          deliveryTime: this.formatDeliveryTimeShort(o.additionalInfo),
          deliveryDays: o.deliveryDays,
          isBestInGroup: (minHomeDays === 0 || o.deliveryDays === minHomeDays) && o.price === fastestCheapest,
        })),
      });
    }

    return groups;
  });

  constructor() {
    effect(
      () => {
        this.selectedOptionInternal.set(this.selectedOption());
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      const selected = this.selectedOptionInternal();
      if (selected) {
        this.selectedOptionChange.emit(selected);
      }
    });
  }

  private extractDeliveryDays(additionalInfo: any): number {
    const min = parseInt(additionalInfo.deliveryTimeMin, 10) || 0;
    const max = parseInt(additionalInfo.deliveryTimeMax, 10) || 0;
    return min || max || 0;
  }

  private buildServiceLabel(estimate: ShippingEstimate, additionalInfo: any): string {
    if (additionalInfo.productName) {
      const name = additionalInfo.productName.toLowerCase();
      if (name.includes('clasico') || name.includes('clásico')) {
        return 'Clásico';
      }
      if (name.includes('expreso')) {
        return 'Expreso';
      }
    }

    return additionalInfo.serviceDescription || estimate.deliveryMode || 'Envío';
  }

  private formatDeliveryTimeShort(additionalInfo: any): string {
    const min = parseInt(additionalInfo.deliveryTimeMin, 10) || 0;
    const max = parseInt(additionalInfo.deliveryTimeMax, 10) || 0;

    if (!min && !max) return '';

    const minDate = this.addBusinessDays(new Date(), min || max);
    const maxDate = this.addBusinessDays(new Date(), max || min);

    const locale = this.t.getActiveLang() === 'en' ? 'en-US' : 'es-AR';
    const formatShort = (d: Date) => {
      const day = d.getDate();
      const weekday = d.toLocaleDateString(locale, { weekday: 'short' }).replace('.', '');
      const month = d.toLocaleDateString(locale, { month: 'short' }).replace('.', '');
      return `${weekday} ${day} ${month}`;
    };

    const arrivesLabel = this.t.translate('shippingOptions.arrivesLabel');

    if (min === max || !min || !max) {
      return `${arrivesLabel} ${formatShort(maxDate)}`;
    }

    return `${arrivesLabel} ${formatShort(minDate)} - ${formatShort(maxDate)}`;
  }

  private addBusinessDays(date: Date, days: number): Date {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const dayOfWeek = result.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        added++;
      }
    }
    return result;
  }

  onOptionSelected(estimate: ShippingEstimate): void {
    this.selectedOptionInternal.set(estimate);
  }

  private safeParseJson(json: string | undefined): Record<string, unknown> {
    if (!json) return {};
    try {
      return JSON.parse(json);
    } catch {
      return {};
    }
  }
}
